// Utility functions for managing user bookmarks
import React from 'react';
import api from '../services/api';
import { MenuItem } from '../components/Layout/Sidebar';

export interface BookmarkItem {
  title: string;
  to: string;
  fullPath?: string | null;
  icon?: React.ReactNode;
}

/** Legacy or renamed routes → current sidebar paths */
const BOOKMARK_PATH_ALIASES: Record<string, string> = {
  '/accounting/documents': '/accounting/journals',
};

export function normalizeBookmarkPath(path: string): string {
  const trimmed = (path || '').trim().replace(/\/+$/, '') || '/';
  return BOOKMARK_PATH_ALIASES[trimmed] ?? trimmed;
}

function normalizeBookmarkItem(bookmark: BookmarkItem): BookmarkItem {
  const to = normalizeBookmarkPath(bookmark.to);
  if (to === bookmark.to) return bookmark;
  return { ...bookmark, to };
}

const BOOKMARKS_CACHE_KEY = 'ums_bookmarks_cache';
const LEGACY_BOOKMARKS_CACHE_KEY = 'ums_favorites_cache';

let bookmarksFetchPromise: Promise<BookmarkItem[]> | null = null;
const BOOKMARKS_MEMORY_TTL_MS = 30_000;
let bookmarksMemoryCache: { data: BookmarkItem[]; at: number } | null = null;

export const getCachedBookmarks = (): BookmarkItem[] => {
  try {
    let cached = localStorage.getItem(BOOKMARKS_CACHE_KEY);
    if (!cached) {
      cached = localStorage.getItem(LEGACY_BOOKMARKS_CACHE_KEY);
    }
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Error reading bookmarks cache:', error);
  }
  return [];
};

const setCachedBookmarks = (bookmarks: BookmarkItem[]): void => {
  try {
    const serializableBookmarks = bookmarks.map((item) => ({
      title: item.title,
      to: normalizeBookmarkPath(item.to),
      fullPath: item.fullPath || null,
    }));
    localStorage.setItem(BOOKMARKS_CACHE_KEY, JSON.stringify(serializableBookmarks));
    localStorage.removeItem(LEGACY_BOOKMARKS_CACHE_KEY);
  } catch (error) {
    console.warn('Error saving bookmarks cache:', error);
  }
};

export const clearBookmarksCache = (): void => {
  bookmarksFetchPromise = null;
  bookmarksMemoryCache = null;
  try {
    localStorage.removeItem(BOOKMARKS_CACHE_KEY);
    localStorage.removeItem(LEGACY_BOOKMARKS_CACHE_KEY);
  } catch (error) {
    console.warn('Error clearing bookmarks cache:', error);
  }
};

export const getBookmarks = async (): Promise<BookmarkItem[]> => {
  const token = localStorage.getItem('access_token');
  if (!token) {
    clearBookmarksCache();
    return [];
  }

  const now = Date.now();
  if (bookmarksMemoryCache && now - bookmarksMemoryCache.at < BOOKMARKS_MEMORY_TTL_MS) {
    return bookmarksMemoryCache.data;
  }

  if (bookmarksFetchPromise) {
    return bookmarksFetchPromise;
  }

  const doFetch = async (): Promise<BookmarkItem[]> => {
    try {
      const response = await api.get('/profile/bookmarks');
      let bookmarks: BookmarkItem[] = [];
      if (response.data && Array.isArray(response.data.data)) {
        bookmarks = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        bookmarks = response.data;
      } else if (Array.isArray(response)) {
        bookmarks = response;
      }
      bookmarks = bookmarks.map(normalizeBookmarkItem);
      setCachedBookmarks(bookmarks);
      bookmarksMemoryCache = { data: bookmarks, at: Date.now() };
      return bookmarks;
    } catch (error: any) {
      console.error('Error fetching bookmarks:', error);
      if (
        error.response?.status === 401 ||
        error.message?.includes('401') ||
        error.message?.includes('Unauthorized') ||
        error.message?.includes('expired')
      ) {
        clearBookmarksCache();
        return [];
      }
      return getCachedBookmarks();
    } finally {
      bookmarksFetchPromise = null;
    }
  };

  bookmarksFetchPromise = doFetch();
  return bookmarksFetchPromise;
};

const saveBookmarks = async (bookmarks: BookmarkItem[]): Promise<boolean> => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('User not authenticated. Please log in.');
    }

    const serializableBookmarks = bookmarks.map((item) => ({
      title: item.title,
      to: normalizeBookmarkPath(item.to),
      fullPath: item.fullPath || null,
    }));

    setCachedBookmarks(serializableBookmarks);
    bookmarksMemoryCache = { data: serializableBookmarks, at: Date.now() };

    await api.put('/profile/bookmarks', { bookmarks: serializableBookmarks });
    window.dispatchEvent(new Event('bookmarksUpdated'));
    return true;
  } catch (error: any) {
    if (
      error.response?.status === 401 ||
      error.message?.includes('401') ||
      error.message?.includes('Unauthorized') ||
      error.message?.includes('expired')
    ) {
      throw new Error('Your session has expired. Please log in again to save bookmarks.');
    }
    throw error;
  }
};

export const addToBookmarks = async (item: {
  title: string;
  to: string;
  fullPath?: string;
}): Promise<boolean> => {
  const bookmarks = await getBookmarks();
  if (!bookmarks.find((entry) => entry.to === item.to)) {
    bookmarks.push({
      title: item.title,
      to: item.to,
      fullPath: item.fullPath || null,
    });
    return saveBookmarks(bookmarks);
  }
  return false;
};

export const addMultipleToBookmarks = async (
  items: Array<{ title: string; to: string; fullPath?: string }>,
): Promise<{ success: boolean; added: number; message: string }> => {
  const bookmarks = await getBookmarks();
  const bookmarkRoutes = bookmarks.map((entry) => entry.to);
  const newItems: BookmarkItem[] = items
    .filter((item) => !bookmarkRoutes.includes(item.to))
    .map((item) => ({
      title: item.title,
      to: item.to,
      fullPath: item.fullPath || null,
    }));

  if (newItems.length === 0) {
    return { success: false, added: 0, message: 'All items are already in bookmarks' };
  }

  const updatedBookmarks = [...bookmarks, ...newItems];
  const result = await saveBookmarks(updatedBookmarks);
  return {
    success: result,
    added: newItems.length,
    message: `Added ${newItems.length} item(s) to bookmarks`,
  };
};

export const removeFromBookmarks = async (route: string): Promise<boolean> => {
  try {
    const bookmarks = await getBookmarks();
    const filtered = bookmarks.filter((entry) => entry.to !== route);
    return await saveBookmarks(filtered);
  } catch {
    return false;
  }
};

export const reorderBookmarks = async (bookmarks: BookmarkItem[]): Promise<boolean> => {
  try {
    return await saveBookmarks(bookmarks);
  } catch (error) {
    console.error('Error reordering bookmarks:', error);
    return false;
  }
};

export const isBookmarked = async (route: string): Promise<boolean> => {
  const bookmarks = await getBookmarks();
  return bookmarks.some((entry) => entry.to === route);
};

export const getAllMenuItems = (
  menuItems: MenuItem[],
): Array<{ title: string; to: string; icon?: React.ReactNode; fullPath: string }> => {
  const items: Array<{ title: string; to: string; icon?: React.ReactNode; fullPath: string }> = [];

  const flattenItems = (itemsArray: MenuItem[], parentTitle = '') => {
    itemsArray.forEach((item) => {
      if (item.name === 'Bookmarks') return;

      const fullPathPrefix = parentTitle ? `${parentTitle} > ${item.name}` : item.name;

      if (item.views && item.views.length > 0) {
        item.views.forEach((view) => {
          items.push({
            title: item.views!.length > 1 ? `${item.name} – ${view.label}` : item.name,
            to: view.path,
            icon: view.icon ?? item.icon,
            fullPath: item.views!.length > 1 ? `${fullPathPrefix} > ${view.label}` : fullPathPrefix,
          });
        });
      } else if (item.path) {
        items.push({
          title: item.name,
          to: item.path,
          icon: item.icon,
          fullPath: fullPathPrefix,
        });
      }

      if (item.children && item.children.length > 0) {
        flattenItems(item.children, fullPathPrefix);
      }
    });
  };

  flattenItems(menuItems);
  return items;
};

function findMenuItemByRoute(items: MenuItem[], route: string): MenuItem | undefined {
  for (const item of items) {
    if (item.path === route) return item;
    if (item.views) {
      const view = item.views.find((v) => v.path === route);
      if (view) return item;
    }
    if (item.children) {
      const found = findMenuItemByRoute(item.children, route);
      if (found) return found;
    }
  }
  return undefined;
}

export const reconstructBookmarkIcon = (
  bookmark: BookmarkItem,
  allMenuItems: MenuItem[],
): React.ReactNode | undefined => {
  const route = normalizeBookmarkPath(bookmark.to);
  const findIconByRoute = (items: MenuItem[], path: string): React.ReactNode | undefined => {
    for (const item of items) {
      if (item.path === path) return item.icon;
      if (item.views) {
        const view = item.views.find((v) => v.path === path);
        if (view) return item.icon ?? view.icon;
      }
      if (item.children) {
        const icon = findIconByRoute(item.children, path);
        if (icon) return icon;
      }
    }
    return undefined;
  };
  return findIconByRoute(allMenuItems, route);
};

export type BookmarkMenuItem = MenuItem & { isBookmark: true };

export function bookmarkToMenuItem(bookmark: BookmarkItem, allMenuItems: MenuItem[]): BookmarkMenuItem {
  const normalized = normalizeBookmarkItem(bookmark);
  const path = normalized.to;
  const menuMatch = findMenuItemByRoute(allMenuItems, path);
  const icon = reconstructBookmarkIcon(normalized, allMenuItems) ?? menuMatch?.icon;

  return {
    name: menuMatch?.name || normalized.title?.trim() || path,
    path,
    icon,
    isBookmark: true,
  };
}
